import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'

import { AssessmentTemplateStatus, AssessmentType, DISCIPLINE_CODES, Grade, useAssessmentTemplatesStore } from '@pages/content-editor'
import { MAX_PER_PAGE, getFirstQueryValue, toPaginationValue } from '@shared/lib/routes'

import { defineNuxtRouteMiddleware } from '#app'

/**
 * Maps a query value onto an enum member, or `null`. A query string is arbitrary user input:
 * `?type=lol` must land as `null`, never as a bogus value the API then rejects with a `422` (§1),
 * so membership is checked, not cast.
 */
const toEnumValue = <T extends string>(value: unknown, allowed: readonly T[]): T | null => {
  const raw = getFirstQueryValue(value)

  return allowed.find(item => item === raw) ?? null
}

/**
 * `/content/assessments` — query → store filters, mirroring `middleware/api-tokens.ts`.
 *
 * `page` / `per_page` are read and validated off the route query by `useAssessmentTemplatesApi`
 * (query wins over store for those two), so the request works without this. They are mirrored
 * anyway so a deep link or back-navigation leaves `getCurrentPage` / `getPerPage` agreeing with
 * what was requested — which holds because both sides coerce through `toPaginationValue`.
 */
export default defineNuxtRouteMiddleware((to: RouteLocationNormalizedGeneric) => {
  const store = useAssessmentTemplatesStore()

  store.setFilterSearch(getFirstQueryValue(to.query?.search) || null)
  store.setFilterType(toEnumValue<AssessmentType>(to.query?.type, Object.values(AssessmentType)))
  store.setFilterDisciplineCode(toEnumValue<DisciplineCode>(to.query?.discipline_code, DISCIPLINE_CODES))
  store.setFilterGrade(toEnumValue<Grade>(to.query?.grade, Object.values(Grade)))
  store.setFilterStatus(
    toEnumValue<AssessmentTemplateStatus>(to.query?.status, Object.values(AssessmentTemplateStatus)),
  )

  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
})
